import { useState, useEffect, useRef } from 'react';
import { FaCode, FaGlobe, FaMicrochip, FaPaintBrush, FaHandshake, FaCheckCircle, FaPalette, FaHeart } from 'react-icons/fa';
import './Skills.css';

import skillsCover from '../cover/contact cover.png';
import art0 from '../cover/art.jpg';
import art1 from '../cover/art1.jpg';
import art2 from '../cover/art2.jpg';
import art3 from '../cover/art3.jpg';
import art4 from '../cover/art4.jpg';

function Skills() {
  const [visibleSections, setVisibleSections] = useState(new Set());
  const [selectedArt, setSelectedArt] = useState(null);
  const sectionRefs = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set([...prev, entry.target.dataset.section]));
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );
    sectionRefs.current.forEach((ref) => { if (ref) observer.observe(ref); });
    return () => observer.disconnect();
  }, []);

  const skillCategories = [
    {
      icon: <FaCode />,
      title: 'Programming Languages',
      color: '#1f4788',
      desc: 'Foundational languages used for system logic and software development.',
      skills: ['Python', 'C / C++', 'Java', 'JavaScript', 'Verilog / HDL']
    },
    {
      icon: <FaGlobe />,
      title: 'Web Development',
      color: '#5ba3d0',
      desc: 'Building responsive and interactive user interfaces and web applications.',
      skills: ['HTML5 / CSS3', 'ReactJS', 'Node.js', 'Responsive Design']
    },
    {
      icon: <FaMicrochip />,
      title: 'Hardware & Embedded',
      color: '#2d5aa0',
      desc: 'Expertise in circuit design, microcontroller integration, and physical computing.',
      skills: ['Arduino', 'ESP32', 'Circuit Design', 'PCB Layout', 'Embedded C', 'IoT Systems']
    },
    {
      icon: <FaPaintBrush />,
      title: 'Technical Tools',
      color: '#87bfff',
      desc: 'Simulation and design tools essential for engineering analysis.',
      skills: ['VS Code', 'Canva', 'Microsoft Office']
    },
    {
      icon: <FaHandshake />,
      title: 'Professional Soft Skills',
      color: '#1a3a52',
      desc: 'Interpersonal and operational skills that drive successful team projects.',
      skills: ['Problem-Solving', 'Team Collaboration', 'Effective Communication', 'Project Management', 'Adaptability']
    },
  ];

  const hobbies = [
    { id: 1, img: art0, title: 'Artwork 1' },
    { id: 2, img: art1, title: 'Artwork 2' },
    { id: 3, img: art2, title: 'Artwork 3' },
    { id: 4, img: art3, title: 'Artwork 4' },
    { id: 5, img: art4, title: 'Artwork 5' },
  ];

  return (
    <section className="skills-page">
      {/* Hero Header */}
      <div
        className={`skills-hero ${visibleSections.has('header') ? 'visible' : ''}`}
        data-section="header"
        ref={(el) => (sectionRefs.current[0] = el)}
      >
        <div className="hero-cover-wrapper">
          <img src={skillsCover} alt="Skills Cover" className="hero-cover-img" />
          <div className="hero-overlay"></div>
        </div>
        
        <div className="hero-content text-left">
          <h1 className="hero-title">Technical Competencies</h1>
          <p className="hero-subtitle">A comprehensive overview of my technical expertise and professional capabilities</p>
        </div>
        
        <div className="hero-scroll-indicator">
          <div className="hero-mouse"></div>
          <span>Explore Expertise</span>
        </div>
      </div>

      <div className="skills-body-wrapper">
        <div className="digital-grid-bg"></div>
        
        <div className="skills-alternating-body">
          {skillCategories.map((cat, catIndex) => (
            <div
              key={catIndex}
              className={`skill-feature-row ${catIndex % 2 === 0 ? 'row-normal' : 'row-reverse'} ${visibleSections.has(`cat-${catIndex}`) ? 'visible' : ''}`}
              data-section={`cat-${catIndex}`}
              ref={(el) => (sectionRefs.current[catIndex + 1] = el)}
            >
              <div className="feature-content">
                <div className="feature-header">
                  <div className="feature-icon" style={{ color: cat.color }}>
                    {cat.icon}
                  </div>
                  <h3 className="feature-title">{cat.title}</h3>
                </div>
                <p className="feature-desc">{cat.desc}</p>
                <div className="feature-line-accent" style={{ background: cat.color }}></div>
              </div>
              
              <div className="feature-badges">
                <div className="badges-wrapper">
                  {cat.skills.map((skill, index) => (
                    <div key={index} className="premium-badge">
                      <FaCheckCircle className="badge-dot" style={{ color: cat.color }} />
                      <span>{skill}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="feature-bg-accent" style={{ background: cat.color }}></div>
              <div className="feature-corner-decor"></div>
            </div>
          ))}
        </div>

        {/* Other Interests & Hobbies Section */}
        <div 
          className={`hobbies-section ${visibleSections.has('hobbies') ? 'visible' : ''}`}
          data-section="hobbies"
          ref={(el) => (sectionRefs.current[skillCategories.length + 1] = el)}
        >
          <div className="hobbies-header">
            <div className="hobbies-line"></div>
            <h2 className="hobbies-title">
              <FaPalette className="hobbies-icon" /> Other Interests & Hobbies
            </h2>
            <p className="hobbies-subtitle">Beyond engineering, I find inspiration through creative expression and art.</p>
          </div>

          <div className="hobbies-grid">
            {hobbies.map((hobby) => (
              <div 
                key={hobby.id} 
                className="hobby-card"
                onClick={() => setSelectedArt(hobby)}
              >
                <div className="hobby-visual">
                  <img src={hobby.img} alt={hobby.title} />
                  <div className="hobby-overlay">
                    <span className="view-text">View Artwork</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Art Modal */}
      {selectedArt && (
        <div className="art-modal-overlay" onClick={() => setSelectedArt(null)}>
          <div className="art-modal-container" onClick={e => e.stopPropagation()}>
            <button className="art-modal-close" onClick={() => setSelectedArt(null)}>&times;</button>
            <div className="art-modal-content">
              <img src={selectedArt.img} alt={selectedArt.title} className="art-modal-image" />
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default Skills;
